import React from 'react'
import { css } from '@emotion/core'
import { Formik } from 'formik'
import { Button, Input, Table } from '@dw/helix'

const confirmSNGPanelStyles = css`
  background-color: #fff;
  padding: 20px 20px 35px;
  border-radius: 2px;
  box-shadow: 0 1px 7px 0 rgba(0, 0, 0, 0.19), 0 3px 3px 0 rgba(0, 0, 0, 0.13);
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
`

const buttonsStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;

  button:first-of-type {
    margin-right: 20px;
  }
`

export interface MissingValuesPayload {
  targetName?: string
  targetId: string
  targetType: 'ARTICLE' | 'SUPPLIER'
  batchCode?: string
  collectionSizeMatrix?: string
  baseColorMatrix?: string
  secondaryColorMatrix?: string
  serialCode?: string
}

interface MissingValuesFormProps {
  missingValuesErrors: any
  onSubmitting: boolean
  onClose: () => any
  onSubmit: (payloadRows: MissingValuesPayload[]) => any
}

interface MissingValuesErrors {
  code:
    | 'ARTICLE_COMPLEMENTARY_DATA_FIELD_MISSING'
    | 'ARTICLE_COMPLEMENTARY_DATA_MISSING'
    | 'VENDOR_COMPLEMENTARY_DATA_FIELD_MISSING'
    | 'VENDOR_COMPLEMENTARY_DATA_MISSING'
  field: string
  keys: {
    articleId?: string
    vendorId?: string
    vendorName?: string
  }
}

interface FormDataItems {
  articleTable: {
    [articleIdMissingValue: string]: {
      articleId: string
      missingValue: string
      key: string
    }
  }
  vendorTable: {
    [articleIdMissingValue: string]: {
      vendorId: string
      vendorName: string
      missingValue: string
      key: string
    }
  }
}

const tableStyles = css`
  width: 100%;
  margin-bottom: 32px;
`

const inputStyles = css`
  height: 35px;
  margin-right: 16px;
  input {
    height: 32px;
  }
`

const transformErrorToFormData = (errors: any) => {
  return errors.reduce(
    (acc: FormDataItems, error: MissingValuesErrors) => {
      const { articleId = '', vendorId = '', vendorName = '' } = error.keys

      if (error.code === 'ARTICLE_COMPLEMENTARY_DATA_FIELD_MISSING') {
        const key = `${articleId}.${error.field}`
        acc.articleTable[key] = {
          articleId,
          missingValue: error.field,
          key,
        }
      }
      if (error.code === 'ARTICLE_COMPLEMENTARY_DATA_MISSING') {
        ;['serialCode', 'collectionSizeMatrix', 'baseColorMatrix', 'secondaryColorMatrix'].forEach((missingValue: string) => {
          const key = `${articleId}.${missingValue}`
          acc.articleTable[key] = {
            articleId,
            missingValue,
            key,
          }
        })
      }
      if (error.code === 'VENDOR_COMPLEMENTARY_DATA_FIELD_MISSING') {
        const key = `${vendorId}.${error.field}`
        acc.vendorTable[key] = {
          vendorId,
          vendorName,
          missingValue: error.field,
          key,
        }
      }
      if (error.code === 'VENDOR_COMPLEMENTARY_DATA_MISSING') {
        ;['serialCode', 'batchCode'].forEach((missingValue: string) => {
          const key = `${vendorId}.${missingValue}`
          acc.vendorTable[key] = {
            vendorId,
            vendorName,
            missingValue,
            key,
          }
        })
      }
      return acc
    },
    {
      articleTable: {},
      vendorTable: {},
    },
  )
}

const MissingValuesForm: React.FunctionComponent<MissingValuesFormProps> = ({
  missingValuesErrors = [],
  onClose,
  onSubmit,
  onSubmitting,
}) => {
  const parsedItems: FormDataItems = transformErrorToFormData(missingValuesErrors)

  const showArticleTable = Object.values(parsedItems.articleTable).length > 0
  const showVendorTable = Object.values(parsedItems.vendorTable).length > 0

  const validate = (formData: any) => {
    const errors: any = {}
    const charactersAndNumbersRegEx = /^[A-Z0-9]+$/g
    const charactersRegEx = /^[A-Z]+$/g
    const numbersRegEx = /^[0-9]+$/g

    const checkForErrors = (targetType: string, targetId: string, missingValue: string) => {
      const regularExpression = (() => {
        if (missingValue === 'serialCode') return numbersRegEx
        if (targetType === 'articles') return charactersAndNumbersRegEx
        return charactersRegEx
      })()

      if (!formData[targetType][targetId]) {
        if (!errors[targetType]) errors[targetType] = {}
        if (!errors[targetType][targetId]) errors[targetType][targetId] = {}

        errors[targetType][targetId][missingValue] = 'required'
        return
      }

      const formMissingValue = formData[targetType][targetId][missingValue]

      if (!formMissingValue || (formMissingValue && !formMissingValue.match(regularExpression))) {
        if (!errors[targetType]) errors[targetType] = {}
        if (!errors[targetType][targetId]) errors[targetType][targetId] = {}
        errors[targetType][targetId][missingValue] = 'not valid'
      }
    }

    Object.values(parsedItems.articleTable).forEach(({ articleId, missingValue }: any) => {
      checkForErrors('articles', articleId, missingValue)
    })

    Object.values(parsedItems.vendorTable).forEach(({ vendorId, missingValue }: any) => {
      checkForErrors('vendors', vendorId, missingValue)
    })

    return errors
  }

  const onFormSubmit = async (values: any) => {
    const articles = Object.entries(values.articles).map((article: any) => {
      const [articleId, codes] = article

      const payload: MissingValuesPayload = {
        targetId: articleId,
        targetType: 'ARTICLE',
      }

      if (typeof codes.collectionSizeMatrix === 'string') {
        payload.collectionSizeMatrix = codes.collectionSizeMatrix
      }
      if (typeof codes.baseColorMatrix === 'string') {
        payload.baseColorMatrix = codes.baseColorMatrix
      }
      if (typeof codes.secondaryColorMatrix === 'string') {
        payload.secondaryColorMatrix = codes.secondaryColorMatrix
      }
      if (typeof codes.serialCode === 'string') {
        payload.serialCode = codes.serialCode
      }
      return payload
    })

    const vendors = Object.entries(values.vendors).map((vendor: any) => {
      const [vendorId, codes] = vendor

      const payload: MissingValuesPayload = {
        targetName: `${parsedItems.vendorTable[`${vendorId}.batchCode`].vendorName}`,
        targetId: vendorId,
        targetType: 'SUPPLIER',
      }

      if (typeof codes.batchCode === 'string') {
        payload.batchCode = codes.batchCode
      }
      if (typeof codes.serialCode === 'string') {
        payload.serialCode = codes.serialCode
      }

      return payload
    })

    onSubmit([...articles, ...vendors])
  }

  return (
    <div css={confirmSNGPanelStyles}>
      <Formik
        initialValues={{ articles: {}, vendors: {} }}
        validateOnChange={false}
        validate={validate}
        onSubmit={onFormSubmit}
      >
        {({ handleSubmit, setFieldValue, values, errors }: any) => {
          return (
            <form onSubmit={handleSubmit}>
              {showArticleTable && (
                <Table
                  items={Object.values(parsedItems.articleTable)}
                  columns={[
                    { label: 'Article Number', field: 'articleId', width: '188px' },
                    { label: 'Missing value', field: 'missingValue', width: '122px' },
                    {
                      label: 'New value',
                      render: (item: any) => (
                        <Input
                          value={values.articles[item.articleId] && values.articles[item.articleId][item.missingValue]}
                          onChange={event => {
                            setFieldValue(`articles.${item.key}`, event.target.value.toUpperCase())
                          }}
                          css={inputStyles}
                          status={
                            errors.articles &&
                            errors.articles[item.articleId] &&
                            errors.articles[item.articleId][item.missingValue]
                              ? 'error'
                              : 'normal'
                          }
                        />
                      ),
                      width: '134px',
                    },
                  ]}
                  rowKey={'key'}
                  css={tableStyles}
                />
              )}
              {showVendorTable && (
                <Table
                  items={Object.values(parsedItems.vendorTable)}
                  columns={[
                    {
                      label: 'Supplier',
                      field: 'vendorName',
                      render: ({ vendorName }: any) => (
                        <span
                          title={vendorName}
                          css={css`
                            width: 90%;
                            white-space: nowrap;
                            overflow: hidden;
                            text-overflow: ellipsis;
                          `}
                        >
                          {vendorName}
                        </span>
                      ),
                      width: '188px',
                    },

                    { label: 'Missing value', field: 'missingValue', width: '122px' },
                    {
                      label: 'New value',
                      render: (item: any) => (
                        <Input
                          value={values.vendors[item.vendorId] && values.vendors[item.vendorId][item.missingValue]}
                          onChange={event => {
                            setFieldValue(`vendors.${item.key}`, event.target.value)
                          }}
                          css={inputStyles}
                          status={
                            errors.vendors &&
                            errors.vendors[item.vendorId] &&
                            errors.vendors[item.vendorId][item.missingValue]
                              ? 'error'
                              : 'normal'
                          }
                        />
                      ),
                      width: '134px',
                    },
                  ]}
                  rowKey={'key'}
                  css={tableStyles}
                />
              )}
              <div css={buttonsStyles}>
                <Button
                  theme="secondary"
                  onClick={onClose}
                  type="button"
                  css={css`
                    min-width: 172px;
                  `}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  css={css`
                    min-width: 198px;
                  `}
                  disabled={onSubmitting}
                >
                  Save new values
                </Button>
              </div>
            </form>
          )
        }}
      </Formik>
    </div>
  )
}

export default MissingValuesForm
